import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { api } from "@/lib/api";
import { ShieldCheck, ArrowRight, Users } from "lucide-react";

const SOCIAL_LABELS = { instagram: "Instagram", youtube: "YouTube", x: "X", tiktok: "TikTok", facebook: "Facebook", website: "Website" };

// Inline brand SVGs (no icon font, no external CDN). Rendered in lime; sized 14px.
function SocialIcon({ k }) {
  const cls = "w-3.5 h-3.5 shrink-0";
  const stroke = { className: cls, viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: 2, strokeLinecap: "round", strokeLinejoin: "round" };
  switch (k) {
    case "instagram":
      return (<svg {...stroke}><rect x="2" y="2" width="20" height="20" rx="5" /><circle cx="12" cy="12" r="4.2" /><circle cx="17.6" cy="6.4" r="1.1" fill="currentColor" stroke="none" /></svg>);
    case "facebook":
      return (<svg className={cls} viewBox="0 0 24 24" fill="currentColor"><path d="M13.5 21v-7h2.4l.4-2.9h-2.8V9.2c0-.85.24-1.43 1.46-1.43H16.4V5.16A20 20 0 0 0 13.87 5c-2.1 0-3.55 1.29-3.55 3.66v2.04H8v2.9h2.32V21z" /></svg>);
    case "youtube":
      return (<svg className={cls} viewBox="0 0 24 24" fill="currentColor"><path d="M23 12s0-3.2-.4-4.7a2.5 2.5 0 0 0-1.76-1.77C19.28 5.13 12 5.13 12 5.13s-7.28 0-8.84.4A2.5 2.5 0 0 0 1.4 7.3C1 8.8 1 12 1 12s0 3.2.4 4.7a2.5 2.5 0 0 0 1.76 1.77c1.56.4 8.84.4 8.84.4s7.28 0 8.84-.4a2.5 2.5 0 0 0 1.76-1.77C23 15.2 23 12 23 12zM9.75 15.02V8.98L15.5 12z" /></svg>);
    case "x":
      return (<svg className={cls} viewBox="0 0 24 24" fill="currentColor"><path d="M18.24 2.25h3.31l-7.23 8.26 8.5 11.24h-6.66l-5.22-6.82-5.97 6.82H1.66l7.73-8.84L1.25 2.25h6.83l4.71 6.23zm-1.16 17.52h1.83L7.01 4.13H5.05z" /></svg>);
    case "tiktok":
      return (<svg className={cls} viewBox="0 0 24 24" fill="currentColor"><path d="M16.6 5.82a4.28 4.28 0 0 1-1.06-2.82h-3.02v12.06a2.44 2.44 0 1 1-2.44-2.44c.2 0 .4.03.59.08V9.62a5.52 5.52 0 0 0-.59-.03A5.47 5.47 0 1 0 15.55 15V9.01a7.28 7.28 0 0 0 4.25 1.36V7.35a4.28 4.28 0 0 1-3.2-1.53z" /></svg>);
    default: // website
      return (<svg {...stroke}><circle cx="12" cy="12" r="9" /><path d="M3 12h18" /><path d="M12 3c2.6 2.7 2.6 15.3 0 18M12 3c-2.6 2.7-2.6 15.3 0 18" /></svg>);
  }
}

// Turn a stored social value (handle or URL) into the visible handle text.
function socialDisplay(k, v) {
  let s = (v || "").trim();
  if (k === "website") return s.replace(/^https?:\/\//i, "").replace(/^www\./i, "").replace(/\/+$/, "");
  if (/^https?:\/\//i.test(s)) {
    const parts = s.replace(/\/+$/, "").split("/").filter(Boolean);
    s = parts[parts.length - 1] || s;
  }
  return s.replace(/^@/, "");
}
const AMBER = "#C2571F"; // AMBASSADOR — Recognition family, rendered separately from Verification
const LIME = "#cfe86b";  // signature-line accent (locked token)

// Peel a trailing "#hashtag" line off a bio so it can render as a lime signature.
function splitBio(bio) {
  const raw = bio || "";
  const lines = raw.split("\n");
  let lastIdx = -1;
  for (let i = lines.length - 1; i >= 0; i--) { if (lines[i].trim()) { lastIdx = i; break; } }
  if (lastIdx >= 0 && /^#\S+$/.test(lines[lastIdx].trim())) {
    return { body: lines.slice(0, lastIdx).join("\n").trimEnd(), signature: lines[lastIdx].trim() };
  }
  return { body: raw, signature: null };
}

// Recruitment pitch cards — invite-only, NO application CTA. Shown in both states.
const PITCH = [
  { k: "What you get", slug: "you", v: "Ambassador badge on your profile, placement on this page, comped membership. (Final offer terms TBD.)" },
  { k: "What we ask", slug: "we", v: "Real field photography, your honest use of the platform, and your name next to ours. (Final terms TBD.)" },
  { k: "Who we want", slug: "who", v: "Our ambassadors are hand-picked — people whose gear actually leaves the safe. Hunters, competitors, professionals." },
];
function PitchCards({ className = "" }) {
  return (
    <div className={`grid sm:grid-cols-3 gap-5 ${className}`}>
      {PITCH.map((p) => (
        <div key={p.k} className="border border-sniper bg-surface p-6" data-testid={`ambassadors-pitch-${p.slug}`}>
          <span className="block font-mono text-[10px] uppercase tracking-[2px] text-[#E8C46A] mb-2.5">{p.k}</span>
          <p className="text-sm text-secondary-foreground font-body leading-relaxed">{p.v}</p>
        </div>
      ))}
    </div>
  );
}

// Shared page hero — renders ABOVE everything in BOTH states (pitch + live roster).
// "// AMBASSADORS" JetBrains Mono lime label · "AMBASSADORS" Barlow Condensed heading · tagline.
function AmbassadorsHero() {
  return (
    <section className="mb-12 sm:mb-16" data-testid="ambassadors-hero">
      <span className="font-mono text-[11px] uppercase tracking-[3.4px]" style={{ color: LIME }} data-testid="ambassadors-hero-label">// AMBASSADORS</span>
      <h1 className="mt-4 font-heading text-4xl sm:text-5xl lg:text-6xl font-black uppercase tracking-tight leading-[1.02]" data-testid="ambassadors-hero-heading">
        AMBASSADORS
      </h1>
      <p className="mt-5 max-w-[620px] text-base md:text-lg text-secondary-foreground font-body leading-relaxed" data-testid="ambassadors-hero-tagline">
        People who run their gear hard and trust OpticSwap when it's time to trade out.
      </p>
    </section>
  );
}


// STATE A — roster hidden (no published ambassadors yet). Invite-only: NO application CTA anywhere.
function RosterHidden() {
  return (
    <section data-testid="ambassadors-pitch">
      <p className="max-w-[560px] text-base text-secondary-foreground font-body leading-relaxed">
        The ambassador roster is coming. Ambassadors are hand-picked and invite-only — until the first is published, this page shows only the story, no empty grid.
      </p>
      <div className="mt-8">
        <Link to="/feed" data-testid="ambassadors-browse-cta"
          className="inline-flex items-center gap-2 border border-sniper text-foreground font-heading font-bold uppercase text-sm px-6 py-3.5 hover:border-olive-bright transition-colors">
          Browse marketplace <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
      <PitchCards className="mt-12" />
    </section>
  );
}

// STATE B — single ambassador component (repeats per entry, order rotated daily by the backend).
function AmbassadorCard({ amb, index }) {
  const photos = amb.photos && amb.photos.length ? amb.photos : [];
  const hero = photos[0];
  const isTeam = amb.entry_type === "team";
  // Socials — Instagram may be a LIST of up to 2 handles (personal + brand); everything else is a
  // single string. Flatten into individual renderable links.
  const socialLinks = [];
  for (const [k, v] of Object.entries(amb.socials || {})) {
    if (k === "instagram") {
      (Array.isArray(v) ? v : (v ? [v] : [])).forEach((h, i) => {
        if (h) socialLinks.push({ k, v: h, key: `instagram-${i}` });
      });
    } else if (v) {
      socialLinks.push({ k, v, key: k });
    }
  }
  const { body: bioBody, signature: bioSig } = splitBio(amb.bio);

  return (
    <div className="border border-sniper bg-surface grid md:grid-cols-[440px_1fr]" data-testid={`ambassador-card-${index}`}>
      {/* photos — every image links into the ambassador's dedicated page */}
      <div className="bg-obsidian p-1.5 self-start" data-testid={`ambassador-photocol-${index}`}>
        <Link to={`/ambassadors/${amb.slug}`}
          className="relative block w-full bg-black overflow-hidden group" style={{ aspectRatio: "4 / 5" }}
          data-testid={`ambassador-hero-link-${index}`}>
          {hero ? (
            <img src={hero} alt={amb.display_name} className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-[1.03]" data-testid={`ambassador-hero-${index}`} />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-muted-foreground font-mono text-xs">NO PHOTO</div>
          )}
          {photos.length > 0 && (
            <span className="absolute bottom-2 right-2 font-mono text-[10px] uppercase tracking-[1.5px] text-white bg-black/60 px-2 py-1 opacity-0 group-hover:opacity-100 transition-opacity">View gallery →</span>
          )}
        </Link>
        {photos.length > 1 && (
          <div className="mt-1.5 flex gap-1.5 overflow-x-auto pb-1" data-testid={`ambassador-thumbs-${index}`}>
            {photos.slice(0, MAX_THUMBS).map((p, i) => (
              <Link key={i} to={`/ambassadors/${amb.slug}?p=${i}`}
                className="shrink-0 w-16 h-16 overflow-hidden border border-sniper hover:border-olive-bright transition-colors"
                data-testid={`ambassador-thumb-${index}-${i}`}>
                <img src={p} alt="" className="w-full h-full object-cover" />
              </Link>
            ))}
          </div>
        )}
      </div>
      {/* bio */}
      <div className="p-7 sm:p-9">
        <span className="inline-block font-mono text-[10px] uppercase tracking-[2px] text-white px-3 py-1 mb-3" style={{ background: AMBER }} data-testid={`ambassador-badge-${index}`}>
          {isTeam ? "Ambassador Team" : "Ambassador"}
        </span>
        <h2 className="font-heading text-3xl sm:text-4xl font-black uppercase tracking-tight leading-none">
          <Link to={`/ambassadors/${amb.slug}`} className="hover:text-olive-bright transition-colors" data-testid={`ambassador-name-link-${index}`}>
            {amb.display_name}
          </Link>
        </h2>
        {isTeam && amb.team_members?.length > 0 && (
          <p className="mt-2 inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-[1.5px] text-secondary-foreground" data-testid={`ambassador-team-${index}`}>
            <Users className="w-3.5 h-3.5 text-olive-bright" /> {amb.team_members.join(" · ")}
          </p>
        )}
        {amb.location && (
          <div className="mt-2 font-mono text-[11px] uppercase tracking-[2px] text-muted-foreground">{amb.location}</div>
        )}
        {(amb.specialties || []).length > 0 && (
          <div className="mt-4 flex flex-wrap gap-2">
            {amb.specialties.slice(0, 3).map((s) => (
              <span key={s} className="border border-olive-bright text-volt font-mono text-[10px] uppercase tracking-[1.5px] px-3 py-1">{s}</span>
            ))}
          </div>
        )}
        <p className="mt-5 max-w-[560px] text-sm sm:text-[15px] text-secondary-foreground font-body leading-relaxed whitespace-pre-line">{bioBody}</p>
        {bioSig && (
          <p className="mt-4 font-mono text-[11px] uppercase tracking-[2.5px]" style={{ color: LIME }} data-testid={`ambassador-signature-${index}`}>{bioSig}</p>
        )}
        {socialLinks.length > 0 && (
          <div className="mt-6 flex flex-wrap gap-2" data-testid={`ambassador-socials-${index}`}>
            {socialLinks.map(({ k, v, key }) => {
              const href = k === "instagram"
                ? (/^https?:\/\//i.test(v) ? v : `https://instagram.com/${v.replace(/^@/, "")}`)
                : (v.startsWith("http") ? v : `https://${v}`);
              return (
                <a key={key} href={href} target="_blank" rel="noopener noreferrer"
                  title={SOCIAL_LABELS[k] || k}
                  className="inline-flex items-center gap-2 border border-[#2E2F31] bg-black/20 px-3.5 py-2 hover:border-olive-bright/60 transition-colors"
                  data-testid={`ambassador-social-${index}-${key}`}>
                  <span style={{ color: LIME }} className="inline-flex items-center"><SocialIcon k={k} /></span>
                  <span className="font-mono text-[11px] tracking-[0.5px] text-foreground lowercase">{socialDisplay(k, v)}</span>
                </a>
              );
            })}
          </div>
        )}
        {amb.linked_verified && amb.linked_username && (
          <div className="mt-6 font-body text-xs text-muted-foreground inline-flex items-center gap-1.5" data-testid={`ambassador-verified-${index}`}>
            <ShieldCheck className="w-3.5 h-3.5 text-[#4E8A1F]" />
            <span className="text-[#4E8A1F] font-semibold">ID VERIFIED</span>
            <Link to={`/u/${amb.linked_username}`} className="hover:text-foreground">@{amb.linked_username}</Link>
          </div>
        )}
      </div>
    </div>
  );
}

const MAX_THUMBS = 12;

export default function Ambassadors() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/ambassadors").then(({ data }) => setData(data)).catch(() => setData({ roster_shown: false, ambassadors: [] }));
  }, []);

  return (
    <div className="min-h-screen text-foreground" data-testid="ambassadors-page">
      <Navbar />
      <div className="max-w-[1100px] mx-auto px-4 sm:px-6 py-16 sm:py-20">
        {!data ? (
          <div className="py-24 text-center font-mono text-xs uppercase tracking-[2px] text-muted-foreground">Loading…</div>
        ) : (
          <>
            <AmbassadorsHero />
            {!data.roster_shown ? (
              <RosterHidden />
            ) : (
              <div data-testid="ambassadors-roster">
                <div className="space-y-8">
                  {data.ambassadors.map((a, i) => (
                    <AmbassadorCard key={a.ambassador_id} amb={a} index={i} />
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
